import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useCallback, useContext, useMemo } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useFeatureGate } from '../../contexts/FeatureGateContext';
import * as api from '../../services/api';
import useToaster from '../useToaster';
import { FEATURE_GATE_TYPES } from '../../constants';
import useAccount from './useAccount';
import useBranches from './useBranches';

const usePublications = ({ publicationIds, enable = true }) => {
  const { accountId } = useAccount();
  const { branches } = useBranches();
  const { checkFeatureGate } = useFeatureGate();
  const { addSuccess, addError } = useToaster();
  const { t } = useContext(ConfigContext);

  const storeKey = ['publications', accountId, publicationIds];
  const { isLoading, data } = useQuery(
    storeKey,
    () =>
      Promise.all(
        publicationIds.filter(Boolean).map(async (publicationId) => {
          return { publicationId, data: await api.fetchPublication({ accountId, publicationId }) };
        })
      ),
    { cacheTime: 0, enabled: enable }
  );

  const publicationsMap = useMemo(
    () =>
      (data || []).reduce(
        (prev, { publicationId, data: { publication } }) => ({ ...prev, [publicationId]: publication }),
        {}
      ),
    [data]
  );

  const { mutateAsync: notifyPublications } = useMutation(
    (params) =>
      Promise.all(
        publicationIds?.map((publicationId) => api.notifyPublication({ accountId, publicationId, ...params }))
      ),
    {
      throwOnError: true,
      onError: () => addError(t('notifyFailure')),
      onSuccess: () => addSuccess(t('notifySuccess'))
    }
  );

  const { mutateAsync: notifyBulkPublications } = useMutation(
    (params) => api.notifyBulkPublication({ accountId, publicationIds, ...params }),
    {
      throwOnError: true,
      onError: () => addError(t('notifyFailure')),
      onSuccess: () => addSuccess(t('notifySuccess'))
    }
  );

  const { mutateAsync: updatePublicationsRaw } = useMutation(
    ({ kind, items, branches: selectedBranches, ...params }) => {
      const updatedPublication = items.filter(({ publicationId }) => publicationId);
      const addedPublication = items.filter(({ publicationId }) => !publicationId);

      const promises = [];

      if (addedPublication.length > 0) {
        promises.push(
          api.addPublication({
            accountId,
            kind,
            branches: selectedBranches ?? branches.map(({ id }) => id),
            items: addedPublication,
            isGradualPublish: params.notification?.publish === 'publishGradually',
            ...params
          })
        );
      }

      if (updatedPublication.length > 0) {
        promises.push(
          ...updatedPublication.map((item) => api.updatePublication({ accountId, kind, ...item, ...params }))
        );
      }

      return Promise.all(promises);
    }
  );

  const updatePublications = useCallback(
    (...params) => checkFeatureGate(FEATURE_GATE_TYPES.AVAILABILITY_CHECK, updatePublicationsRaw)(...params),
    [checkFeatureGate, updatePublicationsRaw]
  );

  return {
    isLoading,
    publicationsMap,
    updatePublications,
    notifyPublications,
    notifyBulkPublications
  };
};

export default usePublications;
